<template>
    <flex-container>
        <input-group :model-value="assistent?.brand?.websiteUrl ?? 'Geen website ingesteld'">
            <template #input>
                <text-input disabled>
                    <template v-if="favicon" #prefix>
                        <img
                            :src="favicon"
                            alt="favicon"
                        />
                    </template>
                </text-input>
            </template>
        </input-group>
        <text-decorator :size="FontSize.xs">
            Aantal gevonden pagina's: {{ pageCount }}
        </text-decorator>
        <flex-container justify-content="space-between">
            <flex-container
                direction="column"
                size="auto"
                :gap="Size.xs"
            >
                <text-decorator :size="FontSize.xs" weight="medium">
                    Laatste crawl
                </text-decorator>
                <text-decorator :size="FontSize.xs">
                    {{ lastCrawledAt }}
                </text-decorator>
            </flex-container>
            <flex-container
                direction="column"
                size="auto"
                :gap="Size.xs"
            >
                <text-decorator :size="FontSize.xs" weight="medium">
                    Eerstvolgende crawl
                </text-decorator>
                <text-decorator :size="FontSize.xs">
                    {{ nextCrawlAt }}
                </text-decorator>
            </flex-container>
        </flex-container>
        <horizontal-line no-margin />
        <flex-container
            justify-content="flex-end"
            align-items="center"
            :gap="null"
        >
            <flex-container size="auto" align-items="center">
                <text-decorator :size="FontSize.xs" weight="medium">
                    Website toedienen:
                </text-decorator>
                <flex-container size="auto">
                    <input-group
                        v-model="assistent.behavior.websiteEnabled"
                        inline
                    >
                        <template #input>
                            <switch-input />
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>
            <text-decorator :size="FontSize.xs">
                {{ assistent.behavior.websiteEnabled ? 'Ingeschakeld' : 'Uitgeschakeld' }}
            </text-decorator>
        </flex-container>
    </flex-container>
</template>

<script setup lang="ts">
    import {
        FlexContainer,
        FontSize,
        InputGroup,
        Size,
        SwitchInput,
        TextDecorator,
    } from '@keyvue/one-ui';
    import { type Assistent, CrawlResult } from '@keyvue/one-api-client';
    import { computed, onMounted, type Ref, ref, watch } from 'vue';
    import useClient from '@/services/keyvue-api/composables/useClient';

    const props = defineProps<{ modelValue: Assistent }>();
    const emit = defineEmits<{
        (e: 'update:modelValue', value: Assistent): void;
    }>();

    const assistent = ref(props.modelValue) as Ref<Assistent>;
    const favicon = ref();
    const pageCount = ref(0);

    const { client } = useClient();
    const crawlResult = ref<CrawlResult>();

    function formatDate(date: Date) {
        const formattedDate = date.toLocaleDateString('nl-NL', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
        const formattedTime = date.toLocaleTimeString('nl-NL', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
        return `${formattedDate} om ${formattedTime} uur`;
    }

    const lastCrawledAt = computed(() => {
        if (!crawlResult.value) return 'In afwachting';

        const lastCrawlDate = new Date(crawlResult.value.crawledAt);
        return formatDate(lastCrawlDate);
    });

    const nextCrawlAt = computed(() => {
        if (!crawlResult.value) return 'In afwachting';

        const nextCrawlDate = new Date(crawlResult.value.crawledAt);
        nextCrawlDate.setDate(nextCrawlDate.getDate() + 1);
        return formatDate(nextCrawlDate);
    });

    watch(props.modelValue, (value) => {
        if (props.modelValue !== value) {
            assistent.value = value;
        }
    });

    watch(assistent, (value: Assistent) => {
        if (assistent.value !== value) {
            emit('update:modelValue', value);
        }
    }, { deep: true });

    onMounted(async () => {
        const params = {
            pagination: true,
            itemsPerPage: 1,
            brand: assistent.value.brand.id,
        };

        params['order[crawledAt]'] = 'desc';

        const crawlResultCollection = await client.getCollection<CrawlResult>(CrawlResult, params);
        pageCount.value = crawlResultCollection.total;
        crawlResult.value = crawlResultCollection.data.pop();
    });
</script>
